// @flow
import * as React from 'react';

import type { ChangeHandler } from 'reshaped';

export function useSearchField<
  T: {
    +firstname?: mixed,
    +lastname?: mixed,
    +name?: mixed,
    +orgname?: mixed,
    ...
  },
>(
  list: Array<T>,
  filters: Array<$Keys<T>>,
): {
  filteredList: Array<T>,
  searchTerm: string,
  handleFilter: ChangeHandler<string>,
} {
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  const filteredList = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return list;
    }

    const normalizedSearch = searchTerm.toLowerCase().trim();
    const priorityList: Array<T> = [];
    const secondaryList: Array<T> = [];

    list.forEach(element => {
      let isPriority = false;
      let isMatch = false;

      const hasOrgName =
        typeof element.orgname === 'string' && element.orgname.length > 0;

      // 1. Check base fields
      for (const field of filters) {
        const isMaskedField =
          hasOrgName && (field === 'firstname' || field === 'lastname');
        const fieldValue = element[field];

        if (fieldValue != null) {
          const val = String(fieldValue).toLowerCase();

          if (val.startsWith(normalizedSearch)) {
            if (!isMaskedField) {
              isPriority = true;
              break; // Best match found, stop checking fields
            }
          }

          if (val.includes(normalizedSearch)) {
            isMatch = true;
          }
        }
      }

      // 2. Check combined names if not already a priority match
      if (
        !isPriority &&
        filters.includes('firstname') &&
        filters.includes('lastname')
      ) {
        const { firstname, lastname } = element;

        if (typeof firstname === 'string' && typeof lastname === 'string') {
          const firstLast = `${firstname} ${lastname}`.toLowerCase();
          const lastFirst = `${lastname} ${firstname}`.toLowerCase();

          const startsWith =
            firstLast.startsWith(normalizedSearch) ||
            lastFirst.startsWith(normalizedSearch);

          if (!hasOrgName && startsWith) {
            isPriority = true;
          } else if (
            firstLast.includes(normalizedSearch) ||
            lastFirst.includes(normalizedSearch)
          ) {
            isMatch = true;
          }
        }
      }

      // 3. Distribute into buckets
      if (isPriority) {
        priorityList.push(element);
      } else if (isMatch) {
        secondaryList.push(element);
      }
    });

    return [...priorityList, ...secondaryList];
  }, [list, searchTerm, filters]);

  const handleFilter = React.useCallback<ChangeHandler<string>>(({ value }) => {
    setSearchTerm(value || '');
  }, []);

  return {
    filteredList,
    searchTerm,
    handleFilter,
  };
}
